/*
 * Beylora search query rule (shopper audit round 2, row 2, 2026-10-24).
 *
 * The store's search returns almost every product for any word: "oval" listed 533 of 534 products
 * (49 rings are oval), "halo" 534, and "asdfgh" 203, because unquoted words are matched loosely
 * against every product's text. The same words in quotes are matched exactly ("oval" 60, "asdfgh" 0).
 * So every search the shopper starts is sent with each specific word in quotes, and a search link that
 * arrives without quotes (a typed address, an old link) is sent once to the quoted address.
 *
 * Words every product shares ("engagement rings", "diamond", "lab grown", "2 carat") are not sent
 * as words, because they would match only the products whose text happens to say them (the quoted
 * "engagement rings" matched 88 of 427 rings). They pick the product type instead:
 *   ring, rings, engagement        -> product_type:"Engagement Ring"
 *   band, bands, wedding           -> product_type:"Wedding Band"
 *   nothing specific left          -> the engagement rings
 * One diamond shape word ("oval") becomes the shape tag (tag:oval), which lists exactly the rings of that shape.
 * Carat words and the number before them are dropped: every ring comes in every carat size.
 *
 * Loaded (deferred) by snippets/search-modal.liquid, which is on every page, the search page included.
 * Plain script, no module.
 * The search page prints the shopper's words by taking the quotes and the type filter back out
 * (sections/search-results.liquid and blocks/_search-input.liquid).
 *
 * Round 3 (2026-10-24):
 *  - The words the shopper typed ride along in the address as w=<words> (only when they differ from the rewritten
 *    query). The page puts them back in the box, the "no results" line and the tab title, so "band" reads "band"
 *    and not the filter it turns into. A w that does not rewrite to exactly the page's own q is ignored, so the
 *    address can never show words the search did not run.
 *  - A last word that is the start of one site word (ova, hal, eme, ban) is finished before it is sent, so
 *    typing "oval" in the overlay does not pass through "No results found for ova".
 *  - The same rule runs the overlay's suggestions (assets/predictive-search.js), so the overlay and the page
 *    list the same products.
 */
(function () {
  'use strict';
  if (window.beyloraSearchQuery) return;

  var QUOTES = /["\u201c\u201d]/g;
  var RING_TYPE = 'product_type:"Engagement Ring"';
  var BAND_TYPE = 'product_type:"Wedding Band"';
  var RING_WORDS = { ring: 1, rings: 1, engagement: 1 };
  /* "wedding ring" means a band; only "engagement" makes a ring and a band both wanted */
  var BAND_WORDS = { band: 1, bands: 1, wedding: 1 };
  var CARAT_WORDS = { ct: 1, cts: 1, carat: 1, carats: 1 };
  /* a diamond shape is a tag on each ring ("Oval Engagement Ring"), which is exact: tag:oval lists the 49 oval rings */
  var SHAPES = { round: 1, oval: 1, emerald: 1, cushion: 1, pear: 1, marquise: 1, radiant: 1, princess: 1, asscher: 1, heart: 1 };
  /* words a shopper is likely to be partway through typing; a last word that starts exactly one of them is finished */
  var VOCAB = [
    'round', 'oval', 'emerald', 'cushion', 'elongated', 'mine', 'pear', 'marquise', 'radiant', 'princess', 'asscher', 'heart',
    'halo', 'solitaire', 'engagement', 'wedding', 'ring', 'rings', 'band', 'bands', 'carat', 'carats',
    'yellow', 'white', 'rose', 'gold', 'platinum', 'bezel', 'eternity', 'vintage'
  ];
  /* two-word shapes: "old mine" and "elongated cushion" are one shape each, tagged "Old Mine Engagement Ring" and
     "Elongated Cushion Engagement Ring". The tag search matches from the start of the tag, so tag:"old mine" lists them. */
  var PAIRS = { 'old mine': 1, 'elongated cushion': 1 };
  var FILLER = {
    cut: 1, cuts: 1, shaped: 1, shape: 1, shapes: 1,
    diamond: 1, diamonds: 1, lab: 1, grown: 1, labgrown: 1, 'lab-grown': 1, created: 1,
    a: 1, an: 1, the: 1, and: 1, for: 1, of: 1, with: 1, in: 1, on: 1, my: 1, me: 1, to: 1, buy: 1, shop: 1
  };

  /** The one site word the last typed word is the start of (3 letters or more), else the word itself. */
  function finish(word) {
    var key = word.toLowerCase();
    if (key.length < 3 || !/^[a-z]+$/.test(key)) return word;
    var found = [];
    for (var i = 0; i < VOCAB.length; i++) {
      if (VOCAB[i] === key) return word;
      if (VOCAB[i].indexOf(key) === 0) found.push(VOCAB[i]);
    }
    if (!found.length) return word;
    found.sort(function (a, b) { return a.length - b.length; });
    /* ring and rings, band and bands: the shortest is the root of the others, so it still counts as one word */
    for (var j = 1; j < found.length; j++) if (found[j].indexOf(found[0]) !== 0) return word;
    return found[0];
  }

  /**
   * Turns what the shopper typed into the query the store should run.
   * "oval rings" becomes "oval" product_type:"Engagement Ring"; "wedding bands" becomes
   * product_type:"Wedding Band". A query that already has quotes or a field (id:, product_type:)
   * is returned unchanged.
   */
  function lexical(raw) {
    var text = String(raw == null ? '' : raw);
    if (text.indexOf('"') > -1 || /(^|\s)[a-z_.]+:\S/i.test(text)) return text;
    var words = text.replace(QUOTES, ' ').replace(/([a-z])-([a-z])/gi, '$1 $2').trim().split(/\s+/);
    var anyWord = false;
    var lastIndex = -1;
    var skipNext = false;
    for (var k = words.length - 1; k >= 0; k--) {
      if (words[k].replace(/^[^\w$]+|[^\w$]+$/g, '')) { lastIndex = k; break; }
    }
    var specific = [];
    var shapes = [];
    var ring = false;
    var engagement = false;
    var band = false;
    for (var i = 0; i < words.length; i++) {
      var word = words[i].replace(/^[^\w$]+|[^\w$]+$/g, '');
      if (skipNext) { skipNext = false; continue; }
      if (!word) continue;
      /* only the word still being typed is finished, and only when the text does not end in a space */
      if (i === lastIndex && !/\s$/.test(text)) word = finish(word);
      anyWord = true;
      var key = word.toLowerCase();
      var next = (words[i + 1] || '').toLowerCase().replace(/[^\w.]+/g, '');
      var pairWord = (i + 1 < words.length) ? words[i + 1].replace(/^[^\w$]+|[^\w$]+$/g, '') : '';
      if (pairWord && i + 1 === lastIndex && !/\s$/.test(text)) pairWord = finish(pairWord);
      var pair = key + ' ' + pairWord.toLowerCase();
      if (PAIRS[pair]) {
        shapes.push(pair);
        skipNext = true;
        continue;
      }
      if (RING_WORDS[key]) {
        ring = true;
        if (key === 'engagement') engagement = true;
      } else if (BAND_WORDS[key]) band = true;
      else if (CARAT_WORDS[key] || /^\d+(?:\.\d+)?-?(?:ct|cts|carat|carats)$/.test(key)) continue;
      else if (/^\d+(?:\.\d+)?$/.test(key) && CARAT_WORDS[next]) continue;
      else if (FILLER[key]) continue;
      else if (SHAPES[key]) shapes.push(word);
      else specific.push('"' + word + '"');
    }
    if (!anyWord) return '';
    var parts = specific.slice();
    var shapeTag = false;
    if (shapes.length === 1 && !band) {
      parts.unshift(shapes[0].indexOf(' ') > -1 ? 'tag:"' + shapes[0].toLowerCase() + '"' : 'tag:' + shapes[0].toLowerCase());
      shapeTag = true;
    } else {
      for (var j = 0; j < shapes.length; j++) parts.push('"' + shapes[j] + '"');
    }
    if (band && engagement) {
      if (!parts.length) parts.push(RING_TYPE + ' OR ' + BAND_TYPE);
    } else if (band) {
      parts.push(BAND_TYPE);
    } else if (!shapeTag && (ring || !parts.length)) {
      parts.push(RING_TYPE);
    }
    return parts.join(' ');
  }

  /** The words as the shopper typed them: the quotes are only for the store. */
  function plain(raw) {
    return String(raw == null ? '' : raw).replace(QUOTES, '').replace(/\s+/g, ' ').trim();
  }

  function isSearchPath(path) {
    return /\/search\/?$/.test(String(path || '').split('?')[0]);
  }

  var TYPED_PARAM = 'w';
  var TYPED_MAX = 120;

  /** The typed words for the address's own query, or '' when the address carries none that match it. */
  function typedFromUrl() {
    try {
      var url = new URL(location.href);
      var q = url.searchParams.get('q');
      var w = plain(url.searchParams.get(TYPED_PARAM));
      if (!q || !w || w.length > TYPED_MAX) return '';
      return lexical(w) === q ? w : '';
    } catch (e) {
      return '';
    }
  }

  /** Search page: put the shopper's own words in the box, the "no results" line and the tab title. */
  function applyTyped() {
    try {
      if (!isSearchPath(location.pathname)) return;
      var typed = typedFromUrl();
      if (!typed) return;
      var input = document.querySelector('input.search-page-input');
      var derived = input ? input.value : '';
      if (input) input.value = typed;
      var spots = document.querySelectorAll('[data-beylora-typed]');
      for (var i = 0; i < spots.length; i++) spots[i].textContent = typed;
      /* the tab title carries the words the page printed: swap them for the typed ones */
      if (derived && derived !== typed && document.title.indexOf(' results for ' + derived) > -1) {
        document.title = document.title.replace(' results for ' + derived, ' results for ' + typed);
      }
    } catch (e) {
      /* the page keeps the words it printed itself */
    }
  }

  /** Search page opened with unquoted words: go once to the quoted address, keeping every other parameter. */
  function canonicalize() {
    try {
      if (!isSearchPath(location.pathname)) return;
      var url = new URL(location.href);
      var q = url.searchParams.get('q');
      if (!q || !q.trim()) return;
      var next = lexical(q);
      if (!next || next === q) return;
      url.searchParams.set('q', next);
      var typed = plain(q);
      if (typed && typed.length <= TYPED_MAX) url.searchParams.set(TYPED_PARAM, typed);
      location.replace(url.toString());
    } catch (e) {
      /* the page still works with the store's own results */
    }
  }

  /** A search form sends the quoted words and leaves the typed words in the box. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!isSearchPath(form.getAttribute('action'))) return;
      var input = form.querySelector('input[name="q"]');
      if (!input) return;
      var next = lexical(input.value);
      if (!next || next === input.value) return;
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'q';
      hidden.value = next;
      input.removeAttribute('name');
      form.appendChild(hidden);
      var typed = plain(input.value);
      var echo = null;
      if (typed && typed.length <= TYPED_MAX) {
        echo = document.createElement('input');
        echo.type = 'hidden';
        echo.name = TYPED_PARAM;
        echo.value = typed;
        form.appendChild(echo);
      }
      /* the form data is read as soon as this handler returns; put the box back right after */
      setTimeout(function () {
        input.setAttribute('name', 'q');
        if (hidden.parentNode) hidden.parentNode.removeChild(hidden);
        if (echo && echo.parentNode) echo.parentNode.removeChild(echo);
      }, 0);
    },
    true
  );

  window.beyloraSearchQuery = { lexical: lexical, plain: plain, canonicalize: canonicalize, applyTyped: applyTyped, typedParam: TYPED_PARAM };
  canonicalize();
  applyTyped();
})();
